// The "thinking" trace behind one chat answer: the hops a message took through the organigram, in
// order. Built from the organigram_process documents the chat dock's live Firestore listener already
// receives - deliberately NOT from GET /requests/{id}/traces, which OrganigramApiService documents as
// SNAPSHOT ONLY (polling it at UI rates costs ~2 + N + N*(1 + M) reads per call).
//
// Plain functions with no Angular dependency, so they can be unit tested without the DI graph.

export interface OrgTraceHop {
  fromId: string;
  toId: string;
  from: string;      // display name of the sender ("You" for the message entering the org)
  to: string;        // display name of the recipient
  message: string;
}

// A process document narrowed to the fields a hop needs.
export interface TraceDoc {
  id?: string;
  request_id?: string;
  node_id?: string;
  sender_node_id?: string;
  message?: string;
  created_at?: any;
}

// Firestore timestamps arrive as {seconds, nanoseconds}; a plain number is already seconds. A
// document with no timestamp sorts first, which keeps the entry hop at the top of the list.
function timeOf(doc: TraceDoc): number {
  const t = doc?.created_at;
  if (!t) { return 0; }
  if (typeof t === 'number') { return t; }
  return (t.seconds || 0) + (t.nanoseconds || 0) / 1e9;
}

// The ordered hops of ONE request. A document with no sender_node_id is the user's own message
// entering the org, so it is attributed to "You". `nameOf` resolves a node id to its label.
export function hopsForRequest(docs: TraceDoc[], requestId: string, nameOf: (id: string) => string): OrgTraceHop[] {
  if (!requestId) { return []; }
  return (docs || [])
    .filter(d => !!d && (d.request_id || d.id) === requestId && !!d.message)
    .sort((a, b) => timeOf(a) - timeOf(b))
    .map(d => ({
      fromId: d.sender_node_id || '',
      toId: d.node_id || '',
      from: d.sender_node_id ? nameOf(d.sender_node_id) : 'You',
      to: nameOf(d.node_id || ''),
      message: d.message || ''
    }));
}

// Copy `next` into `cur` IN PLACE and report whether anything changed. The template tracks hops by
// index, so rows already on screen keep their DOM node and only genuinely new hops animate in. The
// list never shrinks: a trace only grows, and dropping rows mid-run would make it flicker.
export function mergeHops(cur: OrgTraceHop[], next: OrgTraceHop[]): boolean {
  let changed = false;
  for (let i = 0; i < next.length; i++) {
    const a = cur[i];
    const b = next[i];
    if (!a) { cur.push(b); changed = true; continue; }
    if (a.from !== b.from || a.to !== b.to || a.message !== b.message) { cur[i] = b; changed = true; }
  }
  return changed;
}

// Header of the thinking block once the answer has landed. No step count - the block stays closed
// unless someone opens it, so the header is a label, not a readout of what is inside.
export function thinkingLabel(done: boolean, ms?: number): string {
  if (!done) { return LIVE_FALLBACK; }
  const secs = ms && ms > 0 ? Math.max(1, Math.round(ms / 1000)) : 0;
  return secs ? `Thought for ${secs}s` : 'Thought';
}

// Shown while a request is in flight but no hop has been seen yet (the listener may not be up, or
// the first node has not been visited).
export const LIVE_FALLBACK = 'Thinking…';

// The live header for one hop: names who is being asked, so the label reports real progress through
// the org instead of a generic spinner word.
export function hopLabel(hop: OrgTraceHop | undefined): string {
  const name = (hop?.to || '').trim();
  return name ? `Asking ${name}…` : LIVE_FALLBACK;
}

// Every node a trace touched - the graph highlights these cards while the message is in flight.
export function nodeIdsOf(hops: OrgTraceHop[]): string[] {
  const out: string[] = [];
  for (const h of hops || []) {
    if (h.fromId) { out.push(h.fromId); }
    if (h.toId) { out.push(h.toId); }
  }
  return out;
}
